import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  type MovementPattern,
  type PlanStarter,
  type TodayRestHours,
  type Workout as WorkoutT,
  type WorkoutNote,
} from '@heraion/shared';

import { isTodayDraftPending } from './state/loggerDraftHelpers';
import { LoggerHeader } from './LoggerHeader';
import { LoggerDateEditor } from './LoggerDateEditor';
import { HoldCountdownOverlay } from './HoldCountdownOverlay';
import { LoggerModals } from './LoggerModals';
import { useLoggerScreenHost } from './useLoggerScreenHost';
import { useLoggerHeaderState } from './useLoggerHeaderState';
import { LoggerContext } from './loggerContext';
import { EntryHistoryProvider } from './useEntryHistory';
import { WorkoutMetaFooter } from './WorkoutMetaFooter';
import { TimelineLoggerScreen } from './TimelineLoggerScreen';
import type { LoggerHostDeps } from './hostDeps';

export type EditingTarget = {
  /** The saved session. A correction rewrites it in place under this id
   *  (#38); a repeat only reads it. */
  sessionId: string;
  /** Pre-loaded workout shape (assemble of session + sets) used to seed
   *  the draft. */
  workout: WorkoutT;
  /** The queued correction this edit starts from, when the host has a queue
   *  (#314): `workout` is that correction, and the save goes after it. */
  queuedOn?: string;
  /** The session's saved timeline notes, seeded into a correction (#24).
   *  Absent when they could not be read: then none is deleted on save. */
  notes?: readonly WorkoutNote[];
  /** Per-slug movement-pattern map captured at the time the parent
   *  fetched the catalog for the edit. Optional: when missing, edit-
   *  mode saves still succeed but the post-save MAX-bump check skips
   *  entries with unknown patterns (they fall through the rep-trust
   *  cap default). */
  patternsBySlug?: Record<string, MovementPattern | null>;
  /** 'edit' (default) corrects the existing session in place. 'repeat'
   *  clones the structure into a brand-new session: the source row is left
   *  untouched, completedAt / fail are blanked, and startedAt resets to now. */
  mode?: 'edit' | 'repeat';
};

/** Payload the LoggerScreen produces on a save, direct or queued, so the
 *  parent can run the MAX-bump evaluation against the just-saved data. */
export type MaxBumpInput = {
  workout: WorkoutT;
  exercisePatterns: Record<string, MovementPattern | null | undefined>;
  workoutId: string;
  /** Best qualifying e1RM per exercise over the recent corroboration
   *  window, snapshotted from the pre-save history cache. Gates the
   *  downward MAX-regression prompt so one light day can't lower a MAX
   *  below a recent stronger session (Antenore 2026-06-26). */
  recentBestE1RM?: Record<string, number>;
  /** `MX-11` — how many separate sessions in that SAME window carried a
   *  qualifying e1RM. Enough of them with none reaching the stored MAX means
   *  the MAX was never corroborated, and its six-week calendar immunity is
   *  bypassed. Snapshotted from the same walk as the line above. */
  recentSessionCount?: Record<string, number>;
};

/** A save's outcome for the host. It may answer with the exercises it
 *  proposed a new MAX for (#182), which the post-session summary names. */
export type LoggerOnSaved = (
  kind: 'sent' | 'queued',
  maxBumpInput?: MaxBumpInput,
) => readonly string[] | void;

type Props = {
  visible: boolean;
  /** #445: the workout is open, shown or minimized. Arms the idle question
   *  where the host has one (deps.idleWatch). Absent: never asked. */
  active?: boolean;
  onClose: () => void;
  /** When set, the Logger opens pre-populated with the target workout
   *  and saves an edit (write-then-cleanup of the old session). */
  editing?: EditingTarget | null;
  /** When set, the Logger opens pre-populated from a plan starter
   *  (buildPlanStarter output). Plan id + name + notes ride through
   *  to the save payload so the server can flip the plan row to
   *  status=completed. Mutually exclusive with `editing` — call sites
   *  must not pass both; if both are set, editing wins (matches the
   *  web `pendingStart` precedence rule). */
  planStarter?: PlanStarter | null;
  /** Fired after a successful save (direct or queued). Caller refreshes
   *  the workouts list and the outbox-derived counter. maxBumpInput rides
   *  both outcomes (LG-35); consumers feed it to `useMaxBumpOffer`. */
  onSaved: LoggerOnSaved;
  /** Fired when the user taps the header minimize affordance. The
   *  Logger's draft + outbox state stay alive — the caller toggles
   *  `visible` to false so the system Modal hides, then surfaces a
   *  Resume pill (rendered by TabShell) so the user can navigate the
   *  other tabs and come back. Optional: when omitted, the minimize
   *  button is not rendered. */
  onMinimize?: (draftName: string | null) => void;
  /** AP-9b: the pain map, from the «today» draft's veto line. Absent, the
   *  line has no link. */
  onOpenPainMap?: (draftName: string | null) => void;
  /** The empty «today» card's «New workout»: the host opens its create hub. */
  onOpenCreateHub?: () => void;
  /** The empty card's «Build one anyway»: the host builds the draft toward MAV
   *  and hands the starter back, or null when nothing can be built. */
  onBuildAnyway?: () => Promise<PlanStarter | null>;
  /** The draft's rest chips: the host saves the account's choice. */
  onTodayRestHours?: (hours: TodayRestHours) => void;
  /** No muscle is under its weekly minimum: the empty card says so. */
  weeklyMinimumMet?: boolean;
  /** The «today» draft is open and empty (or stopped being): the host loads
   *  the coverage for «Build one anyway» only then. #492 F5: on the
   *  draft's bodyweight-only scope. */
  onTodayEmptyChange?: (empty: boolean, bodyweightOnly?: boolean) => void;
  /** Live draft name (trimmed, null when blank), fired as it changes so the
   *  host can keep what shows the workout (the notification) in step. */
  onNameChange?: (name: string | null) => void;
  /** RW-18 host seam — everything the shared tree cannot reach itself
   *  (Profile, durable write, the exercise-catalog cache, the
   *  host-specific picker/timer-sheet/effort-scale components). See
   *  hostDeps.ts. Mobile's mount supplies its existing modules
   *  unchanged; web-rnw's supplies its own equivalents. */
  deps: LoggerHostDeps;
};

export function LoggerScreen({
  visible,
  active,
  onClose,
  editing = null,
  planStarter = null,
  onSaved,
  onMinimize,
  onOpenPainMap,
  onOpenCreateHub,
  onBuildAnyway,
  onTodayRestHours,
  weeklyMinimumMet,
  onTodayEmptyChange,
  onNameChange,
  deps,
}: Props) {
  const { ctx, cursor, restInvite, minimizeWithName } = useLoggerScreenHost({
    visible,
    active,
    onClose,
    editing,
    planStarter,
    onSaved,
    deps,
    onMinimize,
    onOpenPainMap,
    onOpenCreateHub,
    onBuildAnyway,
    onTodayRestHours,
    weeklyMinimumMet,
    onTodayEmptyChange,
    onNameChange,
  });
  const {
    theme,
    t,
    i18n,
    styles,
    draft,
    dispatch,
    defaultUnit,
    energyReadout,
    postSaveSummary,
    sessionResult,
    summaryCardRef,
    postSaveSharing,
    sharePostSave,
    pickerOpen,
    setPickerOpen,
    effortScaleOpen,
    setEffortScaleOpen,
    swapEntryIdx,
    setSwapEntryIdx,
    tipEntryIdx,
    setTipEntryIdx,
    catalog,
    hold,
    startManualRest,
    restPicker,
    timerDefaults,
    groupPickerEntryIdx,
    setGroupPickerEntryIdx,
    supersetMenu,
    setSupersetMenu,
    addToGroupId,
    setAddToGroupId,
    error,
    serverError,
    usedSlugs,
    groupCatalog,
    plate,
    addMany,
    createSuperset,
    submitting,
    onSave,
    onSaveAsNew,
    onCancel,
    totals,
    mode,
  } = ctx;
  const { tipPlanNote, coachNotes, templateName, inSuperset, onStartRest } =
    useLoggerHeaderState({
      draft,
      apiFetch: deps.apiFetch,
      tipEntryIdx,
      activeId: cursor.activeId,
      startManualRest,
      clearRestInvite: restInvite.clear,
    });

  return (
    <Modal
      visible={visible}
      animationType="slide"
      // Android back minimizes; discarding stays the explicit ✕.
      onRequestClose={minimizeWithName ?? onCancel}
      presentationStyle="pageSheet"
    >
      {/* Modal hosts a separate native view tree on Android that the
          root-level GestureHandlerRootView in App.tsx doesn't reach, so we
          re-root one here. Kept as the standard gesture root for any
          gesture-driven child (the timeline + future surfaces). */}
      <GestureHandlerRootView style={styles.gestureRoot}>
      <EntryHistoryProvider catalog={catalog} apiFetch={deps.apiFetch}>
      <LoggerContext.Provider value={ctx}>
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <LoggerHeader
          postSaveSummary={postSaveSummary}
          mode={mode}
          submitting={submitting}
          entriesLength={draft.entries.length}
          onMinimize={minimizeWithName}
          onSave={onSave}
          onCancel={onCancel}
          error={error}
          serverError={serverError}
          onSaveAsNew={onSaveAsNew}
          onStartRest={onStartRest}
          inSuperset={inSuperset}
          coachNotes={coachNotes}
          templateName={templateName}
          roundType={draft.roundType}
          todayDraft={isTodayDraftPending(draft)}
          dateEditor={
            <LoggerDateEditor
              draft={draft}
              dispatch={dispatch}
              styles={styles}
              theme={theme}
              t={t}
            />
          }
          styles={styles}
          theme={theme}
          t={t}
        />

        {/* The rest banner used to be here, and the hold/cardio timer had one
            of its own beside it. Neither exists any more (MX-5 slices 3 and
            3b): both timers run inside the effort meter's two rows, where the
            athlete pressed. The rest countdown IS the transition to the next
            set now, so it also carries the "→ Set 3/4" caption that used to
            flash on the card for a beat and vanish. Two rows less on screen
            always, three while a timer runs — which is exactly when the screen
            is fullest. */}
        {/* Active logging happens on the timeline (the sole logger). After a
            'sent' save we switch to summary mode: the post-session card +
            CTAs render in a plain scroll view (no autoscroll needed). */}
        {postSaveSummary === null ? (
          <TimelineLoggerScreen />
        ) : (
        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ScrollView
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
          >
            <WorkoutMetaFooter
              postSaveSummary={postSaveSummary}
              sessionResultValue={sessionResult.result}
              energyReadout={energyReadout}
              totals={totals}
              draft={draft}
              postSaveSharing={postSaveSharing}
              sharePostSave={sharePostSave}
              defaultUnit={defaultUnit}
              locale={i18n.language}
              summaryCardRef={summaryCardRef}
              styles={styles}
              theme={theme}
              t={t}
              PostSessionSummaryCard={deps.PostSessionSummaryCard}
              profile={deps.profile}
              resolveName={ctx.getExerciseNameBySlug}
            />
          </ScrollView>
        </KeyboardAvoidingView>
        )}

        <LoggerModals
          animationSource={deps.animationSource}
          onAnimationLoadError={deps.onAnimationLoadError}
          maxMeta={deps.maxesLoaded ? deps.maxMeta : undefined}
          {...(deps.onEditMax ? { onEditMax: deps.onEditMax } : {})}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          addMany={addMany}
          createSuperset={createSuperset}
          usedSlugs={usedSlugs}
          swapEntryIdx={swapEntryIdx}
          setSwapEntryIdx={setSwapEntryIdx}
          effortScaleOpen={effortScaleOpen}
          setEffortScaleOpen={setEffortScaleOpen}
          tipEntryIdx={tipEntryIdx}
          setTipEntryIdx={setTipEntryIdx}
          tipPlanNote={tipPlanNote}
          groupPickerEntryIdx={groupPickerEntryIdx}
          setGroupPickerEntryIdx={setGroupPickerEntryIdx}
          supersetMenu={supersetMenu}
          setSupersetMenu={setSupersetMenu}
          addToGroupId={addToGroupId}
          setAddToGroupId={setAddToGroupId}
          catalog={catalog}
          entries={draft.entries}
          currentMaxes={deps.maxesLoaded ? deps.maxMeta : null}
          location={draft.location ?? null}
          dispatch={dispatch}
          plate={plate}
          restPicker={restPicker}
          timerDefaults={timerDefaults}
          groupCatalog={groupCatalog}
          defaultUnit={defaultUnit}
          theme={theme}
          t={t}
          ExercisePickerMount={deps.ExercisePickerMount}
          ExerciseTimerSheetMount={deps.ExerciseTimerSheet}
          EffortScalePicker={deps.EffortScalePicker}
          saveExerciseGroup={deps.saveExerciseGroup}
          profile={deps.profile}
          PainDot={deps.PainDot}
          usePainRiskLabel={deps.usePainRiskLabel}
          patchProfile={deps.patchProfile}
          notify={deps.notify}
          confirm={deps.confirm}
        />

        {/* 3-2-1-GO intro for the hold timer — full-screen overlay, on top. */}
        <HoldCountdownOverlay intro={hold.intro} />
      </SafeAreaView>
      </LoggerContext.Provider>
      </EntryHistoryProvider>
      </GestureHandlerRootView>
    </Modal>
  );
}

